import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flag, X } from "lucide-react";
import { api, formatApiError } from "../api";
import { formatModerationTimestamp, reportOutcomeKind, reportOutcomeLabel, reportTypeLabel } from "./moderationReports";
import type { AdminMemberMatchReport } from "./moderationMatches";
import {
  isPermanentReportAction,
  ModerationReportActionPicker,
  reportActionDays,
  type ReportActionIndex,
} from "./ModerationReportActionPicker";

type ModerationMatchReportProps = {
  memberId: string;
  report: AdminMemberMatchReport;
};

function isReportActionable(status: string) {
  return status === "pending" || status === "investigating";
}

function buildReviewPayload(actionIndex: ReportActionIndex, reviewNotes?: string) {
  const note = reviewNotes?.trim() || undefined;

  if (actionIndex === 0) {
    return {
      status: "dismissed" as const,
      actionTaken: "none" as const,
      reviewNotes: note,
    };
  }

  if (actionIndex === 1) {
    return {
      status: "resolved" as const,
      actionTaken: "warning" as const,
      reviewNotes: note,
    };
  }

  if (isPermanentReportAction(actionIndex)) {
    return {
      status: "resolved" as const,
      actionTaken: "permanent_ban" as const,
      reviewNotes: note,
    };
  }

  const days = reportActionDays(actionIndex);
  return {
    status: "resolved" as const,
    actionTaken: "temporary_ban" as const,
    reviewNotes: note,
    banDays: days ?? 7,
  };
}

export function ModerationMatchReport({ memberId, report }: ModerationMatchReportProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [actionIndex, setActionIndex] = useState<ReportActionIndex>(1);
  const [reviewNote, setReviewNote] = useState("");

  const review = useMutation({
    mutationFn: (payload: ReturnType<typeof buildReviewPayload>) =>
      api.reviewAdminReport(report.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", memberId] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-matches", memberId] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setExpanded(false);
      setReviewNote("");
    },
  });

  const actionable = isReportActionable(report.status);
  const actionIsPermanent = isPermanentReportAction(actionIndex);

  const openReview = () => setExpanded(true);

  const closeReview = () => {
    setExpanded(false);
    setReviewNote("");
  };

  const onApply = () => {
    if (actionIsPermanent) {
      if (!window.confirm("Permanently suspend this member from matching? They can still sign in, but only an admin can lift it later.")) {
        return;
      }
    }

    review.mutate(buildReviewPayload(actionIndex, reviewNote));
  };

  return (
    <div className="moderation-match-report">
      <div className="moderation-match-report-label-row">
        <p className="moderation-match-report-label">Report</p>
        {!actionable && (
          <span className={`moderation-report-outcome moderation-report-outcome--${reportOutcomeKind(report)}`}>
            {reportOutcomeLabel(report)}
          </span>
        )}
      </div>
      {expanded ? (
        <button
          type="button"
          className="moderation-match-action is-muted"
          disabled={review.isPending}
          onClick={closeReview}
        >
          <X size={14} aria-hidden="true" />
          Cancel
        </button>
      ) : actionable ? (
        <button type="button" className="moderation-match-action" onClick={openReview}>
          <Flag size={14} aria-hidden="true" />
          Review
        </button>
      ) : null}

      <div className="moderation-match-report-reason">
        <span>{reportTypeLabel(report.type)}</span>
        <time dateTime={report.createdAt}>{formatModerationTimestamp(report.createdAt)}</time>
      </div>

      <p className="moderation-match-report-body">{report.description}</p>

      {report.reviewNotes && (
        <p className="moderation-match-report-note">Review note: {report.reviewNotes}</p>
      )}

      {actionable && expanded ? (
          <div className="moderation-report-review-form">
            {review.error && (
              <div className="banner error moderation-report-review-error">
                {formatApiError(review.error)}
              </div>
            )}

            <label className="moderation-report-note-field">
              <span>Note to member</span>
              <textarea
                rows={2}
                value={reviewNote}
                onChange={(event) => setReviewNote(event.target.value)}
                placeholder="Optional note included in the member email"
              />
            </label>

            <ModerationReportActionPicker value={actionIndex} onChange={setActionIndex} />

            <button
              type="button"
              className={`moderation-report-apply-btn ${actionIsPermanent ? "is-danger" : "is-primary"}`}
              disabled={review.isPending}
              onClick={onApply}
            >
              <Flag size={14} aria-hidden="true" />
              {actionIndex === 0
                ? "Dismiss report"
                : actionIsPermanent
                  ? "Suspend permanently"
                  : actionIndex === 1
                    ? "Send warning"
                    : `Suspend ${reportActionDays(actionIndex)} days`}
            </button>
          </div>
      ) : null}
    </div>
  );
}
